import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";

import { DateRangePicker } from "./_components/date-picker/date-range-picker";
import {
  getAllEmployeesBreaksDayWiseQueryOptions,
  getAllEmployeesCheckinsDayWiseQueryOptions,
} from "@/config/queries/query-options";
import type { BreakView } from "@/components/break/types";
import useDate from "./_components/date-picker/useDate";
import { useMemo } from "react";
import { format } from "date-fns";
import { DataTable } from "./_components/checkins-table/data-table";
import { columns } from "./_components/checkins-table/columns";

function areSameDay(checkin: string | Date, checkout: string | Date): boolean {
  const checkinDate = new Date(checkin);
  const checkoutDate = new Date(checkout);

  return (
    checkinDate.getFullYear() === checkoutDate.getFullYear() &&
    checkinDate.getMonth() === checkoutDate.getMonth() &&
    checkinDate.getDate() === checkoutDate.getDate()
  );
}

const DayWiseCheckinsPage = () => {
  const key = Cookies.get("key") as string;

  const { selectedDate } = useDate();

  const { data, isPending } = useQuery(
    getAllEmployeesCheckinsDayWiseQueryOptions(key, selectedDate.toDate())
  );

  const { data: breaksData } = useQuery(
    getAllEmployeesBreaksDayWiseQueryOptions(key, selectedDate.toDate())
  );

  // employee _id → that employee's breaks on the selected day
  const breaksByEmployee = useMemo(() => {
    const map: Record<string, BreakView[]> = {};
    for (const b of breaksData?.breaks ?? []) {
      const id = typeof b.employee === "string" ? b.employee : b.employee?._id;
      if (!id) continue;
      if (!map[id]) map[id] = [];
      map[id].push(b);
    }
    return map;
  }, [breaksData]);

  // console.log("Data", data);

  // const checkinsData = useMemo(() => {
  //   if (isPending) {
  //     return [];
  //   }

  //   if (data === undefined || data?.checkins === undefined) {
  //     return [];
  //   }

  //   const result = data?.checkins?.map((item) => {
  //     return {
  //       employee: item?.employee?.name,
  //       time: item?.createdAt,
  //       mode: item?.mode,
  //     };
  //   });

  //   return result;
  // }, [data, isPending]);

  const testData = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.checkins === undefined) {
      return [];
    }

    const inLogs = data?.checkins?.filter((entry) => entry?.mode === "IN");
    const outLogs = data?.checkins?.filter((entry) => entry?.mode === "OUT");

    const pairedLogs: {
      inLog: {
        employee: {
          _id: string;
          name: string;
        };
        mode: string;
        createdAt: string | Date;
      };
      outLog: {
        employee: {
          _id: string;
          name: string;
        };
        mode: string;
        createdAt: string | Date;
      } | null;
    }[] = [];

    for (const inLog of inLogs) {
      const correspondingOutLog = outLogs.find(
        (outLog) =>
          areSameDay(inLog?.createdAt, outLog?.createdAt) &&
          inLog?.employee?.name === outLog?.employee?.name &&
          !pairedLogs.some((pair) => pair.outLog === outLog) // Check if this outLog has been paired already
      );

      if (correspondingOutLog) {
        pairedLogs.push({ inLog, outLog: correspondingOutLog });
      } else {
        pairedLogs.push({
          inLog,
          outLog: null,
        });
      }
    }

    let result2 = [];

    for (const pair of pairedLogs) {
      if (pair.outLog !== null) {
        const diffInMs = Math.abs(
          new Date(pair?.inLog?.createdAt).getTime() -
            new Date(pair?.outLog?.createdAt).getTime()
        );
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInMinutes = Math.floor(
          (diffInMs % (1000 * 60 * 60)) / (1000 * 60)
        );

        if (diffInHours > 8) {
          const formattedDuration = `${diffInHours} hours and ${diffInMinutes} minutes`;

          result2.push({
            employee: pair?.inLog?.employee?.name,
            breaks: breaksByEmployee[pair?.inLog?.employee?._id] ?? [],
            rawTime: new Date(pair?.inLog?.createdAt),
            checkinTime: format(
              new Date(pair?.inLog?.createdAt as Date),
              "PPpp"
            ),
            checkoutTime: format(
              new Date(pair?.outLog?.createdAt as Date),
              "PPpp"
            ),
            totalWorkedHours: formattedDuration,
            status: "Full Day",
          });
        } else {
          const formattedDuration = `${diffInHours} hours and ${diffInMinutes} minutes`;

          result2.push({
            employee: pair?.inLog?.employee?.name,
            breaks: breaksByEmployee[pair?.inLog?.employee?._id] ?? [],
            rawTime: new Date(pair?.inLog?.createdAt),
            checkinTime: format(
              new Date(pair?.inLog?.createdAt as Date),
              "PPpp"
            ),
            checkoutTime: format(
              new Date(pair?.outLog?.createdAt as Date),
              "PPpp"
            ),
            totalWorkedHours: formattedDuration,
            status: "Not Counting",
          });
        }
      } else {
        const diffInMs = Math.abs(
          new Date(pair?.inLog?.createdAt).getTime() - new Date().getTime()
        );

        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInMinutes = Math.floor(
          (diffInMs % (1000 * 60 * 60)) / (1000 * 60)
        );

        const isFullDay = diffInHours >= 8;

        const formattedDuration = isFullDay
          ? `More than 8 hours or forgot to checkout!`
          : `${diffInHours} hours and ${diffInMinutes} minutes`;

        result2.push({
          employee: pair?.inLog?.employee?.name,
          breaks: breaksByEmployee[pair?.inLog?.employee?._id] ?? [],
          rawTime: new Date(pair?.inLog?.createdAt),
          checkinTime: format(new Date(pair?.inLog?.createdAt as Date), "PPpp"),
          checkoutTime: "",
          totalWorkedHours: formattedDuration,
          status: isFullDay ? "Full Day" : "Still Working",
        });
      }
    }

    return result2.sort((a, b) => b.rawTime.getTime() - a.rawTime.getTime());
  }, [data, isPending, breaksByEmployee]);

  console.log("Test Dat", testData);

  return (
    <div className="p-5 h-[calc(100%-56px)]">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-medium">Checkins of Employees</h3>

        <div className="flex items-center gap-2">
          <DateRangePicker />
        </div>
      </div>

      <div className="mt-5">
        <DataTable data={testData} columns={columns} />
      </div>
    </div>
  );
};

export default DayWiseCheckinsPage;
